import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import styled, { keyframes } from 'styled-components';
import * as Accordion from '@radix-ui/react-accordion';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

import { SuiToggle } from '@splashthat/sui';
import { Badge } from '@splashthat/doppler/Badge';
import NavArrowDown from '@splashthat/doppler/doppler-icons/NavArrowDown';

import { getFormSessionStepsEnabled, toggleSessionsSteps } from 'common/entities/form/sessionRegistration';
import { getFormSessionStep } from 'common/compounds/FormRenderer/selectors';
import { setSessionsStep } from 'common/compounds/FormRenderer/actions';

import { FORM_SESSIONS_STEPS } from 'common/compounds/FormRenderer/constants/sessions';

import { getFormId } from '../../selectors';

import { openEditSessionStepSetting } from '../../actions';
import FormEditorLayout from '../LayoutView/FormEditorLayout';
import { FormEditorRow } from './FormEditorRow';
import ButtonNode from './ButtonNode';

const AccordionHeader = styled(Accordion.Header)`
    && {
        display: flex;
        gap: 8px;
        align-items: center;
        padding-inline: 1rem;
    }
`;

const AccordionTrigger = styled(Accordion.Trigger)`
    display: flex;
    background: transparent;
    margin: 0;
    padding: 0;
    border: none;
`;

const IconDown = styled(NavArrowDown)`
    cursor: pointer;
    transition: transform 300ms;

    ${AccordionHeader}[data-state="open"] & {
        transform: rotate(180deg);
    }
`;

const AccordionHeaderInfo = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    margin-right: auto;

    p {
        flex: 100%;
    }
`;

const slideDown = keyframes`
    from {
        height: 0;
    }
    to {
        height: var(--radix-accordion-content-height);
    }
`;

const slideUp = keyframes`
    from {
        height: var(--radix-accordion-content-height);
    }
    to {
        height: 0;
    }
`;

const AccordionContent = styled(Accordion.Content)`
    overflow: hidden;

    &[data-state='open'] {
        animation: ${slideDown} 300ms cubic-bezier(0.87, 0, 0.13, 1);
    }
    &[data-state='closed'] {
        animation: ${slideUp} 300ms cubic-bezier(0.87, 0, 0.13, 1);
    }
`;

export function FormEditorLayoutSessions() {
    const reduxDispatch = useDispatch();

    const formId = useSelector(getFormId);
    const isSessionsStep = useSelector((state) => getFormSessionStepsEnabled(state, formId));
    const sessionStep = useSelector((state) => getFormSessionStep(state, formId));

    const toggleStep = () => {
        const nextStep =
            sessionStep === FORM_SESSIONS_STEPS.GUEST_INFORMATION
                ? FORM_SESSIONS_STEPS.SESSION_REGISTRATION
                : FORM_SESSIONS_STEPS.GUEST_INFORMATION;

        reduxDispatch(setSessionsStep(nextStep));
    };

    return (
        <Accordion.Root type="single" value={sessionStep} defaultValue={FORM_SESSIONS_STEPS.GUEST_INFORMATION}>
            <Accordion.Item value={FORM_SESSIONS_STEPS.GUEST_INFORMATION}>
                <AccordionHeader className="LayoutNodeContentContainer">
                    <AccordionTrigger onClick={toggleStep}>
                        <VisuallyHidden.Root>Toggle guest information settings</VisuallyHidden.Root>
                        <IconDown />
                    </AccordionTrigger>
                    <AccordionHeaderInfo>
                        <Badge vibe="accent">Step 1</Badge>
                        <p>Guest Information</p>
                    </AccordionHeaderInfo>
                </AccordionHeader>
                <AccordionContent>
                    <FormEditorLayout>
                        {isSessionsStep ? (
                            <FormEditorRow
                                subtitle="Next Button"
                                onClick={() => reduxDispatch(openEditSessionStepSetting(formId, 'nextButton'))}
                            >
                                Next Button
                            </FormEditorRow>
                        ) : (
                            <ButtonNode />
                        )}
                    </FormEditorLayout>
                </AccordionContent>
            </Accordion.Item>
            <Accordion.Item value={FORM_SESSIONS_STEPS.SESSION_REGISTRATION}>
                <AccordionHeader className="LayoutNodeContentContainer">
                    <AccordionTrigger onClick={toggleStep}>
                        <VisuallyHidden.Root>Toggle session registration settings</VisuallyHidden.Root>
                        <IconDown />
                    </AccordionTrigger>
                    <AccordionHeaderInfo>
                        <Badge vibe="accent">Step 2</Badge>
                        <p>Session Registration</p>
                    </AccordionHeaderInfo>
                    <label>
                        <VisuallyHidden.Root>
                            {`${isSessionsStep ? 'Disable' : 'Enable'} session registration`}
                        </VisuallyHidden.Root>
                        <SuiToggle
                            enabled={!!isSessionsStep}
                            onChange={() => reduxDispatch(toggleSessionsSteps(formId, !isSessionsStep))}
                        />
                    </label>
                </AccordionHeader>
                <AccordionContent>
                    <FormEditorRow
                        isDisabled={!isSessionsStep}
                        subtitle="Sessions Headline"
                        onClick={(event) => {
                            event.stopPropagation();
                            reduxDispatch(openEditSessionStepSetting(formId, 'label'));
                        }}
                    >
                        Headline
                    </FormEditorRow>
                    <FormEditorRow
                        isDisabled={!isSessionsStep}
                        subtitle="Sessions settings"
                        onClick={() => reduxDispatch(openEditSessionStepSetting(formId, 'settings'))}
                    >
                        Sessions
                    </FormEditorRow>
                    <FormEditorRow
                        isDisabled={!isSessionsStep}
                        subtitle="Back button"
                        onClick={() => reduxDispatch(openEditSessionStepSetting(formId, 'backButton'))}
                    >
                        Back Button
                    </FormEditorRow>
                    <FormEditorRow
                        isDisabled={!isSessionsStep}
                        subtitle="Submit button"
                        onClick={() => reduxDispatch(openEditSessionStepSetting(formId, 'submitButton'))}
                    >
                        Submit Button
                    </FormEditorRow>
                </AccordionContent>
            </Accordion.Item>
        </Accordion.Root>
    );
}
